import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { useDispatch, useSelector } from 'react-redux'
import { getAgrochemicalPlotlistData, submitAgrochemicalPlotlistData } from '../../../../../actions/carbonfootprint';
import { AgrochemicalLoadingSpinner } from '../../dashboard/shared-components/GraphLoadingSpinner';

export default function AgrochemicalPlotList({ year, groupId }) {
    const dispatch = useDispatch()
    const [agrochemicalPlotlistState, setAgrochemicalPlotlistState] = useState([])
    const [agrochemicalAmountAppliedTotals, setAgrochemicalAmountAppliedTotals] = useState([])
    const loading = useSelector((state) => (state.carbonfootprint.loading))
    const agrochemicalPlotlistData = useSelector((state) => (state.carbonfootprint.agrochemicalPlotlistData))

    useEffect(() => {
        dispatch(getAgrochemicalPlotlistData(groupId, year))
    }, [groupId, year, dispatch])

    useEffect(() => {
        setAgrochemicalPlotlistState(agrochemicalPlotlistData?.data?.rows.map((row) => {
            return {
                plotlist: row.plotlist,
                agrochemicals: row.agrochemicals.map((agrochemical) => ({ ...agrochemical, dirty: false }))
            }
        }))
        setAgrochemicalAmountAppliedTotals(agrochemicalPlotlistData?.agrochemicalTotals)
    }, [dispatch, agrochemicalPlotlistData])

    const handleApplicationRateChange = (e, plotlistId, agrochemicalId) => {
        setAgrochemicalPlotlistState((prevState => prevState.map((plotlistData) => {
            if (plotlistData.plotlist.id === plotlistId) {
                return {
                    plotlist: { ...plotlistData.plotlist, dirty: true },
                    agrochemicals: plotlistData.agrochemicals.map((agrochemical) => {
                        if (agrochemical.agrochemicalId === agrochemicalId) {
                            return {
                                ...agrochemical,
                                rate: e.target.value,
                                dirty: true
                            }
                        }
                        return agrochemical
                    })
                }
            }
            return plotlistData
        })))
    }


    const handleDataSubmission = () => {
        dispatch(submitAgrochemicalPlotlistData(
            agrochemicalPlotlistState.filter((data) => data.plotlist.dirty)
                .map(data => ({
                    plotlist: data.plotlist,
                    agrochemicals: data.agrochemicals.filter(agrochemical => agrochemical.dirty)
                })), groupId, year)).then(() => {
                    dispatch(getAgrochemicalPlotlistData(groupId, year))
                })
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant='h5'>Agrochemical Plotlist Applications</Typography>
            {loading
                ? <AgrochemicalLoadingSpinner />
                : <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TableContainer component={Paper} sx={{ width: '100%' }} >
                            <Table sx={{ minWidth: '100%', width: '100%' }} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">Block Name</TableCell>
                                        {/* <TableCell align="left">Official Land Number</TableCell> */}
                                        <TableCell align="left">Hectares</TableCell>
                                        <TableCell align="left">Primary Crop</TableCell>
                                        {agrochemicalPlotlistData?.data?.agrochemicalHeader?.map((agrochemical, index) => (
                                            <TableCell key={index} align="left">
                                                {agrochemical?.productName} <br />
                                                <b style={{ color: "darkblue" }}>({agrochemical.applicationRateUnit})</b>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {agrochemicalPlotlistState?.map((data) => (
                                        <TableRow
                                            key={data?.plotlist?.id}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell size="small" align="left">{`${data?.plotlist?.block_name} - ${data?.plotlist?.official_land_num}`}</TableCell>
                                            <TableCell align="left">{data?.plotlist?.hectares}</TableCell>
                                            <TableCell align="left">{data?.plotlist?.primary_crop}</TableCell>
                                            {data?.agrochemicals?.map((agrochemical, index) => (
                                                <TableCell key={index} align="left">
                                                    <TextField
                                                        fullWidth
                                                        name={'application rate'}
                                                        label={''}
                                                        value={!!agrochemical.rate ? agrochemical.rate : null}
                                                        onChange={
                                                            (e) => handleApplicationRateChange(e, data?.plotlist.id, agrochemical.agrochemicalId)
                                                        }
                                                    />
                                                </TableCell>

                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Button sx={{ width: 'fit-content' }} variant="contained" onClick={handleDataSubmission}>Submit data</Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='h5'>Agrochemical Applied</Typography>
                        <TableContainer component={Paper} sx={{ width: '100%' }} >
                            <Table sx={{ minWidth: '100%', width: '100%' }} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">Agrochemical</TableCell>
                                        <TableCell align="left">Application Rate Unit</TableCell>
                                        <TableCell align="left">Total Applied</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {agrochemicalAmountAppliedTotals?.map((agrochemical, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell align="left">{agrochemical.productName}</TableCell>
                                            <TableCell align="left">{agrochemical.applicationRateUnit}</TableCell>
                                            <TableCell align="left">
                                                {`${String(agrochemical.totalApplied.toFixed(2))} 
                                                    ${agrochemical.applicationRateUnit === 'kg/ha'
                                                        ? 'kg'
                                                        : agrochemical.applicationRateUnit === 'L/ha'
                                                            ? 'L'
                                                            : ''}`}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>
            }
        </Box>
    );
}
